import type { BackupUrgency, PaletteColour } from '@anytracker/core'

import { lastBackupHeading } from '../../backup/describe.js'
import { useBackupDownload } from '../../backup/useBackupDownload.js'
import { useBackupHealth } from '../../backup/useBackupHealth.js'
import { Button } from '../../components/Button.js'
import { paletteVar } from '../../charts/colours.js'
import {
  Page,
  Row,
  RowLink,
  Section,
  SubHeader,
  Swatch,
  Tile,
} from '../../components/Layout.js'
import { describePersistence, formatBytes } from '../../pwa/persistence.js'
import { useStorageStatus } from '../../pwa/useStorageStatus.js'
import { RangeControl } from '../../range/RangeControl.js'
import styles from './DataScreen.module.css'

const ICON = {
  report: 'M6 3h9l3 3v15H6zM9 12h6M9 16h6M9 8h3',
  sheet: 'M4 5h16v14H4zM4 10h16M4 15h16M10 5v14',
} as const

/**
 * The band's colour follows how overdue the backup is, never how reassuring
 * it would be nice to sound. Calm is the leaf; firm and urgent warm up to the
 * same levels the Home banner uses (T-503).
 */
const BAND: Readonly<Record<BackupUrgency, PaletteColour>> = {
  none: 'leaf',
  gentle: 'leaf',
  firm: 'marigold',
  urgent: 'poppy',
}

/**
 * Export and backup: everything about getting data off this device.
 *
 * Two different errands on one screen, kept visibly apart, because confusing
 * them is the most expensive mistake this app can let someone make. The
 * backup is a file that restores; the report and the CSV are copies for other
 * people and other programs, and neither can be read back in (ADR-0003).
 *
 * The period chosen here is the shared range, so the report and the CSV open
 * on it.
 */
export function DataScreen() {
  const { health, markBackedUp } = useBackupHealth()
  const { download, busy } = useBackupDownload(markBackedUp)
  const { status } = useStorageStatus()

  const colour = BAND[health?.urgency ?? 'none']
  const persistence = status === null ? null : describePersistence(status.state)

  return (
    <Page>
      <SubHeader back={{ to: '/settings', label: 'Settings' }} />
      <h1>Export and backup</h1>

      <section
        className={styles['backup']}
        aria-labelledby="backup-label"
        data-bleed
        data-testid="backup-band"
        style={{
          background: paletteVar(colour, 'tint'),
          borderColor: paletteVar(colour, 'edge'),
        }}
      >
        <h2
          id="backup-label"
          className={styles['eyebrow']}
          style={{ color: paletteVar(colour, 'deep') }}
        >
          <Swatch colour={colour} size={6} />
          Backup
        </h2>
        {health === null ? (
          <p className={styles['body']}>Checking…</p>
        ) : (
          <>
            <p className={styles['heading']} data-testid="backup-heading">
              {lastBackupHeading(health)}
            </p>
            {/*
              Core's words, unsoftened. The file is the only way back if this
              browser's data goes, and nothing here may suggest otherwise.
            */}
            <p className={styles['body']} data-testid="since-last-backup">
              {health.headline}. {health.detail}
            </p>
          </>
        )}
        <Button
          variant="primary"
          fullWidth
          disabled={busy}
          onClick={() => void download()}
        >
          {busy ? 'Preparing…' : 'Back up now'}
        </Button>
      </section>

      <Section label="Share what you have recorded">
        <RowLink
          to="/report"
          title="PDF report"
          subtitle="Charts and summaries, ready to print or send"
          lead={<Tile colour="poppy" path={ICON.report} />}
        />
        <RowLink
          to="/export"
          title="Spreadsheet (CSV)"
          subtitle="Every measurement, one row each. Not a backup — it cannot be read back in"
          lead={<Tile colour="cornflower" path={ICON.sheet} />}
        />
      </Section>

      <section className={styles['period']} aria-labelledby="period-label" data-bleed>
        <h2 id="period-label" className={styles['label']}>
          Period
        </h2>
        <RangeControl look="segmented" />
      </section>

      <Section label="Storage" note={persistence?.detail}>
        <Row
          title="This browser"
          detail={
            persistence === null ? (
              'Checking…'
            ) : (
              <span className={styles['state']} data-testid="persistence-state">
                <Swatch
                  colour={status?.state === 'persistent' ? 'leaf' : 'marigold'}
                  size={6}
                />
                {persistence.label}
              </span>
            )
          }
        />
        {status?.usageBytes !== undefined && (
          <Row
            title="Used on this device"
            // Both numbers, not a percentage. A quota is typically gigabytes and
            // this app stores kilobytes, so every honest percentage rounds to 0%
            // and tells the user nothing.
            detail={
              <span className={styles['mono']} data-testid="storage-usage">
                {formatBytes(status.usageBytes)}
                {status.quotaBytes === undefined
                  ? ''
                  : ` of ${formatBytes(status.quotaBytes)}`}
              </span>
            }
          />
        )}
        <RowLink to="/backup" title="Restore from a backup file" />
      </Section>
    </Page>
  )
}
